import { Icon, Loader, Theme } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import { useEmailVerification } from "../../hooks/useEmailVerification";
import {
  Card,
  CardCenter,
  CodeInput,
  Countdown,
  Description,
  ErrorIcon,
  ErrorText,
  InputGroup,
  InputRow,
  Instructions,
  ResendLink,
  Spinner,
  Title,
} from "./styles";

interface EmailVerifyCardProps {
  /**
   * Called on successful verification so the wizard can re-sync auth claims in
   * place (no page reload) and render the Payment & Shipping form.
   */
  onVerified: () => void | Promise<void>;
}

/**
 * Inline email-verification gate shown on the Payment & Shipping step when the
 * user is not yet verified (email/password signups). Mirrors the standalone
 * verify flow's copy per Figma, auto-submits the 6-digit code, and shows a
 * submitting spinner while verifying.
 */
export const EmailVerifyCard = ({ onVerified }: EmailVerifyCardProps) => {
  const {
    code,
    userEmail,
    submitting,
    hasVerifyCodeError,
    hasSendCodeError,
    codeResent,
    secondsLeft,
    onCodeChange,
    onResendClick,
  } = useEmailVerification({ onVerified });

  return (
    <CardCenter data-testid="pixi-billing-email-verify-card">
      <Card>
        <Title data-testid="pixi-billing-email-verify-title">
          <FormattedMessage
            id="pixi_wizard.verify.title"
            defaultMessage="Please verify your email to setup your billing profile"
          />
        </Title>

        <Description>
          <FormattedMessage
            id="pixi_wizard.verify.description"
            defaultMessage="We've sent you a verification code in an email to"
          />{" "}
          {userEmail}
        </Description>

        <Instructions>
          <FormattedMessage
            id="pixi_wizard.verify.instructions"
            defaultMessage="Copy and paste the 6 digit code into the field below."
          />
        </Instructions>

        <InputGroup>
          <InputRow>
            <CodeInput
              autoFocus
              $error={hasVerifyCodeError}
              data-testid="pixi-billing-email-verify-input"
              name="code"
              placeholder="123456"
              maxLength={6}
              value={code}
              onChange={onCodeChange}
            />
            {submitting ? (
              <Spinner data-testid="pixi-billing-email-verify-spinner">
                <Loader active inline size="small" />
              </Spinner>
            ) : hasVerifyCodeError ? (
              <ErrorIcon data-testid="pixi-billing-email-verify-error-icon">
                <Icon name="warning" color={Theme.color.error} />
              </ErrorIcon>
            ) : null}
          </InputRow>

          {hasVerifyCodeError && (
            <ErrorText data-testid="pixi-billing-email-verify-error">
              <FormattedMessage
                id="pixi_wizard.verify.error.code"
                defaultMessage="Invalid code. Please try again"
              />
            </ErrorText>
          )}
        </InputGroup>

        {/* Single fixed-height slot: link ⇄ countdown ⇄ error never resize the
            card. */}
        {hasSendCodeError ? (
          <ErrorText data-testid="pixi-billing-email-verify-send-failed">
            <FormattedMessage
              id="pixi_wizard.verify.error.sending_code"
              defaultMessage="There was a problem sending the verification email. Please try again later."
            />
          </ErrorText>
        ) : codeResent ? (
          <Countdown data-testid="pixi-billing-email-verify-countdown">
            <FormattedMessage
              id="pixi_wizard.verify.code.resend_countdown"
              defaultMessage="You can resend in {seconds}s"
              values={{ seconds: secondsLeft }}
            />
          </Countdown>
        ) : (
          <ResendLink
            data-testid="pixi-billing-email-verify-resend"
            onClick={onResendClick}
          >
            <FormattedMessage
              id="pixi_wizard.verify.code.resend"
              defaultMessage="Send another code"
            />
          </ResendLink>
        )}
      </Card>
    </CardCenter>
  );
};
